import { fetchInvoiceById } from "@features/invoices/services/invoiceService";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { useState } from "react";
import InvoiceActions from "@features/invoices/components/InvoiceActions";
import { getFileUrl } from "@shared/utils/fileUrl";
import HeadLayout from "@shared/layouts/HeadLayout";

import {
  CalendarIcon,
  CheckCircle2,
  Clock,
  DownloadCloud,
  PrinterIcon,
  ArrowLeft,
  Building2,
  User,
  Phone,
  MapPin,
  Package,
  CreditCard,
  Copy,
  Check,
} from "lucide-react";
import { Button } from "@shared/components/ui/button";
import { Badge } from "@shared/components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@shared/components/ui/card";
import { format } from "date-fns";
import { Separator } from "@shared/components/ui/separator";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import ThreeDotLoader from "@shared/components/ThreeDotLoader";

const INVOICE_STATUS_ICONS = {
  paid: <CheckCircle2 className="size-5 text-green-500" />,
  pending: <Clock className="size-5 text-yellow-500" />,
};

const INVOICE_STATUS_COLORS = {
  paid: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100",
  pending:
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100",
};

const InvoiceDetails = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const {
    data: invoice,
    error,
    isLoading,
  } = useQuery({
    queryKey: ["invoice", id],
    queryFn: () => fetchInvoiceById(id),
  });

  if (isLoading)
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center space-y-4">
          <ThreeDotLoader />
          <p className="text-sm text-muted-foreground">{t("main.loading")}</p>
        </div>
      </div>
    );

  if (error)
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="w-full max-w-md">
          <CardContent className="text-center p-6">
            <div className="text-red-500 mb-4">
              <Package className="size-12 mx-auto" />
            </div>
            <h3 className="font-semibold text-lg mb-2">{t("common.error")}</h3>
            <p className="text-muted-foreground">{error.message}</p>
          </CardContent>
        </Card>
      </div>
    );

  if (!invoice)
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="w-full max-w-md">
          <CardContent className="text-center p-6">
            <div className="text-muted-foreground mb-4">
              <Package className="size-12 mx-auto" />
            </div>
            <h3 className="font-semibold text-lg mb-2">
              {t("invoices.noInvoiceFound")}
            </h3>
            <p className="text-muted-foreground">
              {t("invoices.invoiceNotExists")}
            </p>
          </CardContent>
        </Card>
      </div>
    );

  const handlePrint = () => {
    const printContents = document.getElementById("invoice").innerHTML;

    // Create a new window for printing
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>${t("invoices.invoice")} ${invoice._id}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .no-print { display: none !important; }
            @media print {
              .no-print { display: none !important; }
            }
          </style>
        </head>
        <body>
          ${printContents}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
    printWindow.close();
  };

  const copyInvoiceId = async () => {
    try {
      await navigator.clipboard.writeText(invoice._id);
      setCopied(true);
      toast.success(t("invoices.invoiceIdCopied"));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t("invoices.failedToCopyInvoiceId"));
    }
  };
  return (
    <>
      <HeadLayout title={t("invoices.invoiceDetails")}>
        {/* Header with Navigation and Actions - Compact */}
        <div className="flex flex-wrap items-center justify-between gap-2 no-print">
          <div className="flex flex-wrap items-center gap-1">
            <InvoiceActions
              invoiceId={invoice._id}
              invoice={invoice}
              clientInfo={invoice.client}
              showPDFActions={true}
              showMarkAsPaid={false}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={copyInvoiceId}
              className="hidden sm:flex"
            >
              {copied ? (
                <Check className="size-3" />
              ) : (
                <Copy className="size-3" />
              )}
            </Button>

            {invoice.paymentProof && (
              <Button variant="outline" size="sm" asChild>
                <Link
                  to={getFileUrl(`invoices/${invoice.paymentProof}`)}
                  target="_blank"
                  download
                >
                  <DownloadCloud className="size-3" />
                </Link>
              </Button>
            )}
          </div>
        </div>
      </HeadLayout>

      {/* Invoice Content - Compact */}
      <Card id="invoice" className="max-w-4xl mx-auto">
        <CardContent className="p-4 space-y-4">
          {/* Invoice Header - Compact */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-3 border-b">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-bold text-primary">
                  {t("invoices.invoice").toUpperCase()}
                </h2>
                <Badge
                  variant="secondary"
                  className={`${
                    INVOICE_STATUS_COLORS[invoice.status]
                  } font-medium text-xs`}
                >
                  <div className="ltr:mr-1 rtl:ml-1">
                    {INVOICE_STATUS_ICONS[invoice.status]}
                  </div>
                  {t(`status.${invoice.status}`)}
                </Badge>
              </div>
              <div className="text-xs text-muted-foreground space-y-1">
                <div>#{invoice._id}</div>
                <div>
                  {format(new Date(invoice.createdAt), "dd/MM/yyyy HH:mm")}
                </div>
                {invoice.status === "paid" && invoice.datePayment && (
                  <div className="flex items-center gap-1">
                    <CreditCard className="size-3" />
                    {format(new Date(invoice.datePayment), "dd/MM/yyyy HH:mm")}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Company and Client Information - Compact */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Company Information */}
            <div className="p-3 bg-muted/30 rounded-lg">
              <h4 className="font-semibold text-sm flex items-center gap-1 mb-2">
                <Building2 className="size-4" />
                {t("invoices.fromCompany")}
              </h4>
              <div className="space-y-1">
                <div className="font-bold text-sm text-primary">
                  {t("company.name")}
                </div>
                <div className="text-xs text-muted-foreground space-y-0.5">
                  <div>{t("company.address")}</div>
                  <div>{t("company.email")}</div>
                </div>
              </div>
            </div>

            {/* Client Information */}
            <div className="p-3 bg-muted/30 rounded-lg">
              <h4 className="font-semibold text-sm flex items-center gap-1 mb-2">
                <User className="size-4" />
                {t("invoices.toClient")}
              </h4>
              <div className="space-y-1">
                <div className="font-bold text-sm text-primary">
                  {invoice.client.brandName}
                </div>
                <div className="text-xs text-muted-foreground space-y-0.5">
                  <div>{invoice.client.fullName}</div>
                  <div>{invoice.client.phone}</div>
                  {invoice.client.email && <div>{invoice.client.email}</div>}
                </div>
              </div>
            </div>
          </div>

          {/* Package Details Table - Compact */}
          <div className="space-y-3">
            <h4 className="text-lg font-semibold flex items-center gap-2">
              <Package className="size-4" />
              {t("packages.packageDetails")} ({invoice.packages.length})
            </h4>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-muted">
                    <th className="text-left py-2 px-2 font-semibold text-xs">
                      {t("packages.customerInformation")}
                    </th>
                    <th className="text-center py-2 px-2 font-semibold text-xs">
                      {t("common.city")}
                    </th>
                    <th className="text-center py-2 px-2 font-semibold text-xs">
                      {t("status.status")}
                    </th>
                    <th className="text-right py-2 px-2 font-semibold text-xs">
                      {t("packages.totalCost")}
                    </th>
                    <th className="text-right py-2 px-2 font-semibold text-xs">
                      {t("packages.totalAmount")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.packages.map((item, index) => (
                    <tr
                      key={index}
                      className="border-b border-muted/50 hover:bg-muted/20 transition-colors"
                    >
                      <td className="py-2 px-2">
                        <div className="space-y-0.5">
                          <div className="font-medium text-sm">
                            {item.customer.name}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Phone className="size-3" />
                              {item.customer.phone}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="text-center py-2 px-2">
                        <div className="flex items-center justify-center gap-1">
                          <MapPin className="size-3 text-muted-foreground" />
                          <span className="text-sm font-medium">
                            {item.customer.city}
                          </span>
                        </div>
                      </td>
                      <td className="text-center py-2 px-2">
                        <Badge variant="outline" className="text-xs px-1 py-0">
                          {t(`status.${item.status}`)}
                        </Badge>
                      </td>
                      <td className="text-right py-2 px-2 font-medium text-sm">
                        {item.totalCost} {t("common.currency")}
                      </td>
                      <td className="text-right py-2 px-2 font-medium text-sm">
                        {item.totalAmount.toFixed(2)} {t("common.currency")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Financial Summary - Compact */}
          <div className="flex justify-end pt-3 border-t">
            <div className="w-full max-w-xs bg-muted/30 rounded-lg p-3">
              <h4 className="font-semibold text-sm mb-2">
                {t("invoices.financialSummary")}
              </h4>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground text-xs">
                    {t("invoices.totalGross")}:
                  </span>
                  <span className="font-medium">
                    {invoice.totalAmount.toFixed(2)} {t("common.currency")}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground text-xs">
                    {t("invoices.totalCost")}:
                  </span>
                  <span className="font-medium">
                    {invoice.totalCost} {t("common.currency")}
                  </span>
                </div>
                <div className="border-t pt-1.5">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-sm">
                      {t("invoices.totalNet")}:
                    </span>
                    <span className="font-bold text-primary">
                      {Number(invoice.totalNet).toFixed(2)}{" "}
                      {t("common.currency")}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer - Compact */}
          <div className="text-center text-xs text-muted-foreground pt-3 border-t border-muted">
            <p>
              {t("invoices.generatedOn")}{" "}
              {format(new Date(), "dd/MM/yyyy HH:mm")}
            </p>
            <p className="mt-0.5">
              {t("company.name")} - {t("invoices.invoiceSystem")}
            </p>
          </div>
        </CardContent>
      </Card>
    </>
  );
};

export default InvoiceDetails;
